import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ExternalLink, Plus, Trash2, Upload, Inbox, Images, FileText, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const inputCls = "bg-ink/60 border-white/15 text-white placeholder:text-slate-500 h-11 rounded-xl w-full";
const STATUSES = ["New", "Contacted", "Estimated", "Booked", "Completed"];
const CATEGORIES = ["Interior", "Exterior", "Cabinet"];

function OverviewTab({ quotes, gallery }) {
  const fresh = quotes.filter((q) => q.status === "New").length;
  const cards = [
    { label: "Quote Requests", value: quotes.length, icon: Inbox },
    { label: "New Leads", value: fresh, icon: FileText },
    { label: "Gallery Images", value: gallery.length, icon: Images },
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-4" data-testid="admin-overview">
      {cards.map((c) => (
        <div key={c.label} className="rounded-2xl border border-white/10 bg-card/50 p-6">
          <c.icon className="h-5 w-5 text-sky mb-3" />
          <p className="font-display font-extrabold text-3xl">{c.value}</p>
          <p className="text-sm text-slate-400 mt-1">{c.label}</p>
        </div>
      ))}
      <div className="sm:col-span-3 rounded-2xl border border-white/10 bg-card/50 p-6">
        <p className="font-display font-bold mb-2">Link preview (Google / WhatsApp / Socials)</p>
        <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-ink/60 p-4 max-w-xl">
          <img src="/og-image.png" alt="Link preview" className="w-28 rounded-lg" />
          <div>
            <p className="text-sm font-semibold text-slate-200">Perez Osorio Painting Services | Interior &amp; Exterior Painting</p>
            <p className="text-xs text-slate-500 mt-1">Your logo and business name appear when the link is shared.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContentTab() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!content) return <p className="text-slate-500">Loading content...</p>;

  const setField = (k, v) => setContent({ ...content, [k]: v });
  const setArr = (key, i, field, v) => {
    const arr = [...content[key]];
    arr[i] = { ...arr[i], [field]: v };
    setField(key, arr);
  };

  const scalars = [
    ["business_name", "Business Name"], ["tagline", "Tagline"], ["phone", "Phone"],
    ["service_area", "Service Area"], ["hero_overline", "Hero Overline"], ["insurance_badge", "Insurance Badge Text"],
    ["hero_line1", "Hero Headline Line 1"], ["hero_line2", "Hero Headline Line 2 (blue)"],
    ["hero_line3", "Hero Headline Line 3"], ["about_heading", "About Heading"],
    ["marquee_text", "Marquee Ribbon Text"], ["cta_heading", "Quote Section Heading"], ["cta_sub", "Quote Section Subtext"],
  ];

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/content", content);
      setContent(data);
      toast.success("Site content updated — live immediately");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-content-tab">
      <div className="grid sm:grid-cols-2 gap-4">
        {scalars.map(([k, label]) => (
          <div key={k}>
            <label className="block text-xs font-mono tracking-[0.15em] uppercase text-slate-400 mb-1.5">{label}</label>
            <Input value={content[k] || ""} onChange={(e) => setField(k, e.target.value)} className={inputCls} data-testid={`content-field-${k}`} />
          </div>
        ))}
      </div>
      <div>
        <label className="block text-xs font-mono tracking-[0.15em] uppercase text-slate-400 mb-1.5">Hero Subtitle</label>
        <Textarea value={content.hero_sub || ""} onChange={(e) => setField("hero_sub", e.target.value)} rows={3}
          className="bg-ink/60 border-white/15 text-white rounded-xl" data-testid="content-field-hero_sub" />
        <label className="block text-xs font-mono tracking-[0.15em] uppercase text-slate-400 mb-1.5 mt-4">About Text</label>
        <Textarea value={content.about_text || ""} onChange={(e) => setField("about_text", e.target.value)} rows={3}
          className="bg-ink/60 border-white/15 text-white rounded-xl" data-testid="content-field-about_text" />
      </div>

      <div>
        <p className="font-display font-bold text-lg mb-3">Chapters (The Standard)</p>
        <div className="space-y-4">
          {(content.chapters || []).map((c, i) => (
            <div key={i} className="rounded-2xl border border-white/10 p-4 grid sm:grid-cols-3 gap-3">
              <Input value={c.title} onChange={(e) => setArr("chapters", i, "title", e.target.value)} className={inputCls} placeholder="Title" data-testid={`chapter-title-${i}`} />
              <div className="sm:col-span-2">
                <Textarea value={c.text} onChange={(e) => setArr("chapters", i, "text", e.target.value)} rows={2}
                  className="bg-ink/60 border-white/15 text-white rounded-xl" placeholder="Description" data-testid={`chapter-text-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="font-display font-bold text-lg mb-3">Services</p>
        <div className="space-y-4">
          {(content.services || []).map((s, i) => (
            <div key={i} className="rounded-2xl border border-white/10 p-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={s.title} onChange={(e) => setArr("services", i, "title", e.target.value)} className={inputCls} placeholder="Service title" data-testid={`service-title-${i}`} />
                <Input value={s.image} onChange={(e) => setArr("services", i, "image", e.target.value)} className={inputCls} placeholder="Image URL (https://...)" data-testid={`service-image-${i}`} />
              </div>
              <Textarea value={s.desc} onChange={(e) => setArr("services", i, "desc", e.target.value)} rows={2}
                className="bg-ink/60 border-white/15 text-white rounded-xl" placeholder="Description" data-testid={`service-desc-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="font-display font-bold text-lg">Testimonials</p>
          <button onClick={() => setField("testimonials", [...(content.testimonials || []), { name: "", location: "", text: "" }])}
            className="inline-flex items-center gap-1.5 text-sm text-sky hover:text-white transition-colors" data-testid="admin-add-testimonial-btn">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
        <div className="space-y-4">
          {(content.testimonials || []).map((t, i) => (
            <div key={i} className="rounded-2xl border border-white/10 p-4 space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <Input value={t.name} onChange={(e) => setArr("testimonials", i, "name", e.target.value)} className={inputCls} placeholder="Name" data-testid={`testimonial-name-${i}`} />
                <Input value={t.location} onChange={(e) => setArr("testimonials", i, "location", e.target.value)} className={inputCls} placeholder="Location" data-testid={`testimonial-location-${i}`} />
                <button onClick={() => setField("testimonials", content.testimonials.filter((_, j) => j !== i))}
                  className="justify-self-start sm:justify-self-end inline-flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 transition-colors" data-testid={`testimonial-remove-${i}`}>
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              </div>
              <Textarea value={t.text} onChange={(e) => setArr("testimonials", i, "text", e.target.value)} rows={2}
                className="bg-ink/60 border-white/15 text-white rounded-xl" placeholder="Review text" data-testid={`testimonial-text-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="admin-save-content-btn"
        className="rounded-xl bg-royal px-8 py-3.5 font-display font-bold hover:bg-royalhover transition-colors duration-200 disabled:opacity-60">
        {saving ? "Saving..." : "Save All Changes"}
      </button>
    </div>
  );
}

function GalleryTab({ gallery, reload }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("Interior");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return toast.error("Choose an image first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("category", category);
      await api.post("/gallery/upload", fd);
      toast.success("Image added to gallery");
      setFile(null); setCaption("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addUrl = async () => {
    if (!url.trim().startsWith("https://")) return toast.error("URL must start with https://");
    setBusy(true);
    try {
      await api.post("/gallery/url", { url: url.trim(), caption, category });
      toast.success("Image added to gallery");
      setUrl(""); setCaption("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (item) => {
    await api.patch(`/gallery/${item.id}`, { visible: !item.visible });
    reload();
  };

  const remove = async (item) => {
    await api.delete(`/gallery/${item.id}`);
    toast.success("Image removed");
    reload();
  };

  return (
    <div className="space-y-8" data-testid="admin-gallery-tab">
      <div className="rounded-2xl border border-white/10 bg-card/50 p-6 space-y-4">
        <p className="font-display font-bold">Add Image</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption" className={inputCls} data-testid="gallery-caption-input" />
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} px-3 text-sm border`} data-testid="gallery-category-select">
            {CATEGORIES.map((c) => <option key={c} value={c} className="bg-navy">{c}</option>)}
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm text-slate-400 file:mr-4 file:rounded-lg file:border-0 file:bg-royal file:px-4 file:py-2 file:text-white file:cursor-pointer"
            data-testid="gallery-file-input" />
          <button onClick={upload} disabled={busy} data-testid="admin-add-gallery-btn"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-royal px-6 py-2.5 font-display font-bold hover:bg-royalhover transition-colors disabled:opacity-60">
            <Upload className="h-4 w-4" /> Upload Photo
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-white/10">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="...or paste an image URL (https://...)" className={inputCls} data-testid="gallery-url-input" />
          <button onClick={addUrl} disabled={busy} data-testid="admin-add-gallery-url-btn"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-sky/40 text-sky px-6 py-2.5 font-display font-bold hover:bg-sky/10 transition-colors disabled:opacity-60">
            <Plus className="h-4 w-4" /> Add by URL
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {gallery.map((item) => (
          <div key={item.id} className="rounded-2xl border border-white/10 overflow-hidden bg-card/40" data-testid={`admin-gallery-item-${item.id}`}>
            <img src={item.src} alt={item.caption} className="aspect-[4/3] w-full object-cover" />
            <div className="p-3 space-y-2">
              <p className="text-xs text-slate-300 truncate">{item.caption || "(no caption)"}</p>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <Switch checked={item.visible} onCheckedChange={() => toggle(item)} data-testid={`gallery-visible-${item.id}`} />
                  Visible
                </label>
                <button onClick={() => remove(item)} className="text-red-400 hover:text-red-300 p-1" data-testid={`gallery-delete-${item.id}`} aria-label="Delete image">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuotesTab({ quotes, reload }) {
  const setStatus = async (q, status) => {
    await api.patch(`/quotes/${q.id}`, { status });
    reload();
  };
  const remove = async (q) => {
    await api.delete(`/quotes/${q.id}`);
    toast.success("Request deleted");
    reload();
  };

  if (quotes.length === 0)
    return <p className="text-slate-500" data-testid="quotes-empty">No quote requests yet. New submissions from the website form will appear here.</p>;

  return (
    <div className="space-y-3" data-testid="admin-quotes-tab">
      {quotes.map((q) => (
        <div key={q.id} className="rounded-2xl border border-white/10 bg-card/50 p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`quote-row-${q.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-display font-bold">{q.name}</p>
              <a href={`tel:${q.phone.replace(/\D/g, "")}`} className="text-sky text-sm font-mono" data-testid={`quote-phone-${q.id}`}>{q.phone}</a>
              {q.email && <span className="text-slate-400 text-sm">{q.email}</span>}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {q.service_type} • {q.property_type} • {new Date(q.created_at).toLocaleString()}
            </p>
            {q.message && <p className="text-sm text-slate-300 mt-2">{q.message}</p>}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <select value={q.status} onChange={(e) => setStatus(q, e.target.value)}
              className={`${inputCls} px-3 text-sm border h-10`} data-testid={`quote-status-${q.id}`}>
              {STATUSES.map((s) => <option key={s} value={s} className="bg-navy">{s}</option>)}
            </select>
            <button onClick={() => remove(q)} className="text-red-400 hover:text-red-300 p-2" data-testid={`quote-delete-${q.id}`} aria-label="Delete request">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [gallery, setGallery] = useState([]);

  const reload = useCallback(() => {
    api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/gallery?all=true").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) reload();
  }, [user, reload]);

  if (user === undefined) {
    return <div className="min-h-screen bg-ink flex items-center justify-center text-slate-500">Loading...</div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-navy/70 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="logo" className="h-9 rounded-lg bg-white p-1" />
            <p className="font-display font-bold text-sm">Site Manager</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-sky transition-colors" data-testid="admin-view-site">
              <ExternalLink className="h-4 w-4" /> View Site
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-300 transition-colors" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <Tabs defaultValue="overview">
          <TabsList className="bg-card/60 border border-white/10 mb-8 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="admin-tab-overview"><LayoutDashboard className="h-4 w-4 mr-1.5" />Overview</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content"><FileText className="h-4 w-4 mr-1.5" />Site Content</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="admin-tab-gallery"><Images className="h-4 w-4 mr-1.5" />Gallery</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes"><Inbox className="h-4 w-4 mr-1.5" />Quote Requests</TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><OverviewTab quotes={quotes} gallery={gallery} /></TabsContent>
          <TabsContent value="content"><ContentTab /></TabsContent>
          <TabsContent value="gallery"><GalleryTab gallery={gallery} reload={reload} /></TabsContent>
          <TabsContent value="quotes"><QuotesTab quotes={quotes} reload={reload} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
